import { useCallback, useEffect, useRef, useState } from 'react';
import { Notification } from 'src/shared/types/src/lib/notification.types';
import { APP_MESSAGES } from "src/shared/config/src";
import { useAppDispatch, useAppSelector } from '../../services/store';
import LocalImages from "./../../static";
import { getNotificationsLoading, getNotificationsPaginatedLoading, getNotificationsList, getNotificationsCurrentPage, getNotificationsLastPage, getNotificationsUnreadCount } from 'src/shared/redux-state/src/lib/notifications/NotificationSelector';
import { fetchNotifications, fetchNotificationUnReadCount, markNotificationAsRead } from 'src/shared/redux-state/src/lib/notifications/NotificationHandler';
import { AxiosError } from 'axios';
import { unwrapResult } from '@reduxjs/toolkit';
import { ChildrenStoreData, LoginStorageData } from 'src/shared/types/src';
import { getCurrentChild, getLoginData, updateCurrentChild } from 'src/shared/redux-state/src';
import { LoginPersistActions } from 'src/shared/redux-state/src/lib/login/LoginSlice';
import { useNavigate } from 'react-router-dom';
import BackButtonLayout from 'src/app/layouts/BackButtonLayout';
import { toast } from 'react-toastify';
import { eventTypeImages, handleNavigationFromNotifications } from './notifications.helper';
import { ConfirmationModal, CustomButton, NotificationListContentLoader } from 'src/app/components';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from 'reactstrap';
import { NotificationActions } from 'src/shared/redux-state/src/lib/notifications/NotificationSlice';
import { clearAll, markAllAsRead } from 'src/shared/services/src/lib/notification';
import { HiDotsVertical } from 'react-icons/hi';
import { IoMdSettings } from "react-icons/io";
import { AppRoutes } from 'src/app/navigation/routes';
import { switchCurrentChildIfNeeded } from 'src/app/utilities/helpers';

const NotificationsPage = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [fetchNext, setFetchNext] = useState<boolean>(true);
    const [clearAllConfirmationModal, setClearAllConfirmationModal] = useState<boolean>(false);
    const [markAllReadConfirmationModal, setMarkAllReadConfirmationModal] = useState<boolean>(false);
    const [isConfirming, setIsConfirming] = useState<boolean>(false);

    const lastItemRef = useRef<HTMLDivElement | null>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const loginData: LoginStorageData = useAppSelector(getLoginData);
    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
    const notificationLoading = useAppSelector(getNotificationsLoading);
    const paginatedLoading = useAppSelector(getNotificationsPaginatedLoading);
    const notificationList = useAppSelector(getNotificationsList);
    const currentPage = useAppSelector(getNotificationsCurrentPage);
    const lastPage = useAppSelector(getNotificationsLastPage);
    const unreadCount = useAppSelector(getNotificationsUnreadCount);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggle = () => setDropdownOpen((prevState) => !prevState);

    const fetchNotificationsData = useCallback(async (page: number) => {
        if (!page) page = 1;
        await dispatch(fetchNotifications({ page }))
            .then(unwrapResult)
            .catch((error) => {
                handleAPIError(error);
            })
    }, [dispatch]);

    const handleAPIError = (error: any) => {
        if (error.name === 'AxiosError') {
            const apiError = error as AxiosError;
            if (apiError && apiError.response) {
                const { errors } = error.response.data;
                toast.error((errors ? errors.join(", ") : APP_MESSAGES.generalErrorMessage), { autoClose: 5000 });
            } else {
                toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
            }
        } else {
            toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
    }

    const fetchNextPage = useCallback(async () => {
        if (fetchNext) {
            if (notificationLoading || paginatedLoading) return;
            if ((currentPage === lastPage) || paginatedLoading) {
                setFetchNext(false);
                return;
            } 
            await fetchNotificationsData(currentPage + 1);
        }
    }, [currentPage, fetchNext, fetchNotificationsData, lastPage,
        notificationLoading, paginatedLoading]);

    const init = () => {
        fetchNotificationsData(1);
        dispatch(fetchNotificationUnReadCount());
    };

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (lastItemRef.current) {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            };

            observer.current = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            }, options);

            observer.current?.observe(lastItemRef.current);
        }
        const currentRef = lastItemRef.current;
        const handleObserve = () => {
            if (observer?.current && currentRef) {
                observer.current.unobserve(currentRef);
            }
        };

        return  handleObserve;
    }, [notificationList, fetchNextPage]);

    // const switchCurrentChildIfNeeded = useCallback(async (notification: Notification) => {
    //     let canNavigate = true;
    //     if (!currentChild.notificationKey.includes(notification.notificationKey)) {
    //         const updatedChild: ChildrenStoreData | undefined = loginData.children.find(
    //             (child) => child.notificationKey.includes(notification.notificationKey)
    //         );
    //         if (updatedChild) {
    //             await dispatch(
    //                 updateCurrentChild({ currentChild: updatedChild })
    //             );
    //         } else {
    //             canNavigate = false;
    //             toast.error(APP_MESSAGES.childAssociatedWithThisNotificationNotFound, { autoClose: 5000 });
    //         }
    //     }
    //     return canNavigate;
    // }, [currentChild.notificationKey, dispatch, loginData.children]);

    const handleNotificationClick = useCallback(async (notification: Notification) => {
        await dispatch(LoginPersistActions.setUpdateCurrentChildLoading(true));
        const canNavigate = await switchCurrentChildIfNeeded(notification, currentChild, loginData, dispatch);
        await dispatch(LoginPersistActions.setUpdateCurrentChildLoading(false));

        if (canNavigate) {
            if(!notification.isRead) {
                dispatch(markNotificationAsRead({ id: notification.id }))
                .then(unwrapResult)
                .catch((error) => {
                    handleAPIError(error);
                });
            }

            const eventType = notification.eventType;
            if (eventType) {
                const entityDetails = notification.entityDetails;
                const entityDetailsAsJSONString = entityDetails ? JSON.stringify(entityDetails) : '';
                handleNavigationFromNotifications(navigate, eventType, notification.entityId, entityDetailsAsJSONString);
            }
        }
    }, [dispatch, navigate, switchCurrentChildIfNeeded]);

    const renderFooterLoader = useCallback(() => {
        if (paginatedLoading && currentPage !== lastPage)
            return (
                <div className='d-flex flex-column align-items-center justify-content-center mg-t-20'>
                    <Spinner style={{ width: '20px', height: '20px' }} />
                </div>
            );
        return null;
    }, [paginatedLoading, currentPage, lastPage]);

    const _renderItem = useCallback(({ item }: { item: Notification }) => {
        return (
            <div onClick={() => { handleNotificationClick(item) }} className='notification-item cursor-pointer'>
                <div className='notification-icon'>
                    <img src={eventTypeImages.get(item.eventType)} alt="event-type" />
                </div>
                <div className='notification-content d-flex flex-column justify-content-center w-100'>
                    <div className='notification-title d-flex align-items-start'>
                        <p className={`mg-b-0 fs-12 text-black flex-1 ${item.isRead ? '' : 'fw-semibold'}`}>{item.title}</p> <span className={`fs-10 ${item.isRead ? 'text-gray-300' : 'text-black'}`}>{item.timestamp}</span>
                    </div>
                    <div className={`fs-12 ${item.isRead ? 'text-gray-300' : 'text-black'}`}>{item.message}</div>
                </div>
            </div>
        );
    }, [handleNotificationClick])

    const toggleMarkAllReadConfirmationModal = () => {
        setMarkAllReadConfirmationModal(!markAllReadConfirmationModal);
    }

    const toggleClearAllConfirmationModal = () => {
        setClearAllConfirmationModal(!clearAllConfirmationModal);
    }

    const handleMarkAllAsRead = async () => {
        setIsConfirming(true);
        dispatch(NotificationActions.markAllAsRead());
        try {
            await markAllAsRead();
            toggleMarkAllReadConfirmationModal()
        } catch (error) {
            if (error instanceof AxiosError) {
                const apiError = error as AxiosError;
                if (apiError && apiError.response) {
                    const { errors } = error?.response?.data;
                    toast.error(errors.join(", "), { autoClose: 5000 });
                } else {
                    toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
                }
            }
        } finally {
            setIsConfirming(false)
        }
    }

    const handleClearAll = async () => {
        setIsConfirming(true);
        try {
            await clearAll();
            dispatch(NotificationActions.clearAll());
            toggleClearAllConfirmationModal()
        } catch (error) {
            if (error instanceof AxiosError) {
                const apiError = error as AxiosError;
                if (apiError && apiError.response) {
                    const { errors } = error?.response?.data;
                    toast.error(errors.join(", "), { autoClose: 5000 });
                } else {
                    toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
                }
            }
        } finally {
            setIsConfirming(false)
        }
    }

    const renderLayoutRightHtml = () => (
        <div className='col-sm-2 col-3 text-end notification-settings-icon-wrap'>
            <div className='d-flex align-items-center justify-content-end cursor-pointer'>
                <span 
                    onClick={() => navigate(AppRoutes.NOTIFICATIONS_SETTINGS)}
                    className='d-flex align-items-center justify-content-center notification-settings-icon'>
                    <IoMdSettings />
                </span>
                <Dropdown isOpen={dropdownOpen} toggle={toggle} className='notification-dropdown'>
                    <DropdownToggle color='link'><HiDotsVertical size={18} /></DropdownToggle>
                    <DropdownMenu end>
                        <DropdownItem disabled={unreadCount === 0} onClick={toggleMarkAllReadConfirmationModal} className='border-bottom border-gray-600'>Mark all as read</DropdownItem>
                        <DropdownItem disabled={notificationList?.length === 0} onClick={toggleClearAllConfirmationModal}>Clear all</DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>
        </div>
    )

    return (
        <BackButtonLayout 
            title="Notifications"
            renderLayoutRightHtml={renderLayoutRightHtml}
        >
            {notificationLoading ? (
                <div className='mx-n-20'><NotificationListContentLoader /></div>
            ) : (
                notificationList.length > 0 ? (
                    <div className='mx-n-20'>
                        {notificationList.map((item, index) => (
                            <div key={index}
                                ref={index === notificationList.length - 1 ? lastItemRef : null}
                                className={`pd-x-20 pd-b-12  ${index === notificationList.length - 1 ? '' : 'mg-b-12 border-bottom border-gray-600'}`}>
                                {_renderItem({ item })}
                            </div>
                        ))}
                        {renderFooterLoader()}
                    </div>
                ) : <NoNotifications />
            )}

            {markAllReadConfirmationModal ?
                <ConfirmationModal
                    isModal={markAllReadConfirmationModal}
                    toggleModal={toggleMarkAllReadConfirmationModal}
                    confirmationText={<p className='pd-y-30'>Are you sure you want to mark all the unread notifications as read?</p>}
                    handleConfirmation={handleMarkAllAsRead} 
                    popupMinHeight={'auto'}
                    modalHeader={'Confirm'}
                    primaryBtnText={'Yes'}
                    secondaryBtnText={'Cancel'}
                    isLoading={isConfirming}
                /> :
                null
            }

            {clearAllConfirmationModal ? 
                <ConfirmationModal
                    isModal={clearAllConfirmationModal}
                    toggleModal={toggleClearAllConfirmationModal}
                    confirmationText={<p className='pd-y-30'>Are you sure you want to clear all the notifications?</p>}
                    handleConfirmation={handleClearAll}
                    popupMinHeight={'auto'}
                    modalHeader={'Confirm'}
                    primaryBtnText={'Yes'}
                    secondaryBtnText={'Cancel'}
                    isLoading={isConfirming}
                /> : 
                null
            }
        </BackButtonLayout>
    );
};


const NoNotifications = () => {
    return (
        <div className='no-notifications'>
            <img src={LocalImages.notificationIcon} alt="no-notifications" />
            <p className='text-center fs-16 fw-bold text-black mg-b-0 mg-t-10'>
                No Notifications
            </p>
        </div>
    );
}

export default NotificationsPage;